import {
  type AnyPromptPart,
  type HttpUserPrompt,
  isKnownPromptPart,
} from '@wandb/weave/lib/WbAgent/promptParts';
import type {
  ToolCall,
  ToolCallResponseRecord,
  TurnResponse,
  TurnState,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import type {ContentPart, ReadonlyJSONValue} from '../store/thread';
import type {RuntimeEvent} from './events';

export interface TurnEventExtractorOptions {
  /** skipUserMessages defines whether to drop user message events. This is
   * useful if, for example, the frontend adds them optimistically to its store.
   */
  skipUserMessages?: boolean;
}

/**
 * TurnEventExtractor converts {@link TurnResponse} poll snapshots into
 * {@link RuntimeEvent}s, emitting only events new since the previous call.
 *
 * A turn maps to at most two message nodes: `${turnId}:user` and
 * `${turnId}:assistant`, where the assistant's message contains all parts of
 * its response (e.g. reasoning, text, and tool calls).
 */
export class TurnEventExtractor {
  private lastSeenMessageIndex = 0;

  /**
   * pendingToolCallResults holds call IDs whose `tool-call-result` hasn't been
   * emitted yet — the response record may arrive in a later poll than the
   * declaring message.
   */
  private pendingToolCallResults = new Set<string>();

  /**
   * userMessageEmitted tracks whether we've already seen a user-role message
   * for this turn. The backend creates one turn per user message, so this
   * should never flip more than once — but we guard against it because
   * `turn.user_prompt` is canonical and would be re-emitted otherwise.
   */
  private userMessageEmitted = false;

  constructor(
    private readonly turnId: string,
    private readonly opts?: TurnEventExtractorOptions
  ) {}

  /**
   * extract processes the latest turn snapshot and returns events for new data
   * since the last call.
   */
  extract(turn: TurnResponse): RuntimeEvent[] {
    const events: RuntimeEvent[] = [];

    // Only responses are needed — invocations come from `msg.tool_calls`.
    const responses = new Map<string, ToolCallResponseRecord>();
    for (const toolCall of turn.tool_calls ?? []) {
      if (toolCall.type === 'response') {
        responses.set(toolCall.call_id, toolCall);
      }
    }

    const messages = turn.messages ?? [];
    for (let i = this.lastSeenMessageIndex; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.role == null) {
        continue;
      }

      switch (msg.role) {
        case 'user':
          // The backend creates one turn per user message, so a turn should
          // only ever have a single user-role message. Defend against that
          // invariant changing — `turn.user_prompt` is canonical and would
          // otherwise be re-emitted for each user message in the turn.
          if (this.userMessageEmitted) {
            console.error(
              `[WBAgent] TurnEventExtractor: turn ${this.turnId} has multiple user messages; ignoring subsequent ones`
            );
            break;
          }
          this.userMessageEmitted = true;

          if (!this.opts?.skipUserMessages) {
            events.push({
              type: 'user-message',
              messageId: this.userMessageId,
              content: httpPromptToStoreContent(turn.user_prompt),
            });
          }
          break;

        case 'assistant':
          if (msg.content) {
            events.push(
              ...makeContentEvents(
                'text-message',
                this.assistantMessageId,
                msg.content
              )
            );
          }
          if (msg.tool_calls?.length) {
            for (const toolCall of msg.tool_calls) {
              events.push(
                ...makeToolCallEvents(toolCall, this.assistantMessageId)
              );
              this.pendingToolCallResults.add(toolCall.id);
            }
          }
          break;

        case 'reasoning':
          if (msg.content) {
            events.push(
              ...makeContentEvents(
                'reasoning',
                this.assistantMessageId,
                msg.content
              )
            );
          }
          break;

        case 'tool':
          // Results are emitted from `turn.tool_calls` instead — it carries
          // richer data (structured output + error flag) than this message.
          break;
      }
    }
    this.lastSeenMessageIndex = messages.length;

    // Emit results for any pending calls whose response record is now
    // available. `tool-call-result` mutates an existing part via toolCallId,
    // so late arrival doesn't affect `content[]` ordering.
    for (const callId of this.pendingToolCallResults) {
      const response = responses.get(callId);
      if (!response) {
        continue;
      }

      events.push(makeToolCallResultEvent(response));
      this.pendingToolCallResults.delete(callId);
    }

    return events;
  }

  private get userMessageId(): string {
    return `${this.turnId}:user`;
  }

  private get assistantMessageId(): string {
    return `${this.turnId}:assistant`;
  }
}

/**
 * extractTurnId extracts the turn ID from a composed messageId. MessageIds
 * have the form `${turnId}:<suffix>` (e.g. `${turnId}:assistant`,
 * `${turnId}:user`) — the turn ID is everything before the first `:`. Turn
 * IDs themselves don't contain `:`.
 */
export function extractTurnId(messageId: string): string | null {
  const idx = messageId.indexOf(':');
  return idx > 0 ? messageId.slice(0, idx) : null;
}

/**
 * terminalEventForTurnState maps a backend `TurnState` to the corresponding
 * runtime terminal event. Returns null for non-terminal states (`in_progress`,
 * `queued`).
 */
export function terminalEventForTurnState(
  state: TurnState,
  runId: string,
  errorMessage?: string | null
): RuntimeEvent | null {
  switch (state) {
    case 'errored':
      return {
        type: 'run-error',
        runId,
        message:
          errorMessage || 'The agent encountered an error while processing.',
      };
    case 'cancelled':
      return {type: 'run-cancelled', runId};
    case 'completed':
      return {type: 'run-finished', runId};
    default:
      return null;
  }
}

function makeContentEvents<P extends 'text-message' | 'reasoning'>(
  prefix: P,
  messageId: string,
  content: string
): [
  {type: `${P}-start`; messageId: string},
  {type: `${P}-content`; messageId: string; delta: string},
  {type: `${P}-end`; messageId: string},
] {
  return [
    {type: `${prefix}-start`, messageId},
    {type: `${prefix}-content`, messageId, delta: content},
    {type: `${prefix}-end`, messageId},
  ];
}

function makeToolCallEvents(
  toolCall: ToolCall,
  messageId: string
): [RuntimeEvent, RuntimeEvent, RuntimeEvent] {
  return [
    {
      type: 'tool-call-start',
      toolCallId: toolCall.id,
      toolName: toolCall.function.name,
      messageId,
    },
    {
      type: 'tool-call-args',
      delta: toolCall.function.arguments,
      toolCallId: toolCall.id,
    },
    {type: 'tool-call-end', toolCallId: toolCall.id},
  ];
}

function makeToolCallResultEvent(
  response: ToolCallResponseRecord
): RuntimeEvent {
  const output =
    typeof response.output === 'string'
      ? response.output
      : response.output == null
        ? ''
        : JSON.stringify(response.output);
  return {
    type: 'tool-call-result',
    toolCallId: response.call_id,
    content: output,
    isError: response.is_error ?? false,
  };
}

function httpPromptToStoreContent(prompt: HttpUserPrompt): ContentPart[] {
  if (typeof prompt === 'string') {
    return [{type: 'text', text: prompt, status: 'complete'}];
  }
  return prompt.map(promptPartToStoreContent);
}

function promptPartToStoreContent(part: AnyPromptPart): ContentPart {
  if (!isKnownPromptPart(part)) {
    return {
      type: 'unknown',
      rawType: part.type,
      data: part as unknown as ReadonlyJSONValue,
    };
  }
  switch (part.type) {
    case 'text':
      return {type: 'text', text: part.text, status: 'complete'};
    case 'image':
      return {
        type: 'image',
        mediaType: part.media_type,
        base64Data: part.base64_data,
      };
    case 'wandb_entity':
      return {
        type: 'wandb-entity',
        entityName: part.entity_name,
      };
    case 'wandb_project':
      return {
        type: 'wandb-project',
        entityName: part.entity_name,
        projectName: part.project_name,
      };
    case 'wandb_run':
      return {
        type: 'wandb-run',
        entityName: part.entity_name,
        projectName: part.project_name,
        runName: part.run_name,
        runDisplayNameStatus: 'pending',
      };
    case 'wandb_call':
      return {
        type: 'wandb-call',
        entityName: part.entity_name,
        projectName: part.project_name,
        callId: part.call_id,
      };
    case 'wandb_web_page_state':
      return {
        type: 'wandb-web-page-state',
        url: part.url,
        entityName: part.entity_name ?? undefined,
        projectName: part.project_name ?? undefined,
        content: part.content?.map(c => ({
          type: 'workspace',
          viewId: c.view_id ?? undefined,
          viewName: c.view_name ?? undefined,
          viewType: c.view_type ?? undefined,
          viewScope: c.view_scope ?? undefined,
          runCountTotal: c.run_count_total ?? undefined,
          runCountFiltered: c.run_count_filtered ?? undefined,
          runCountVisualized: c.run_count_visualized ?? undefined,
          runFiltersExpression: c.run_filters_expression ?? undefined,
          panelSearchQuery: c.panel_search_query ?? undefined,
          runSelectorGrouping:
            c.run_selector_grouping?.map(k => ({
              section: k.section,
              name: k.name,
            })) ?? undefined,
        })),
      };
  }
}
